<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import EnDialog from 'Components/base/EnDialog/EnDialog.vue';

import { useScreenSize } from 'Js/mixins/screenSizeComposable';
import EnButton from 'Components/base/EnButton/EnButton.vue';

/**
 * A simple confirmation dialog.
 */
const props = defineProps({
  /**
   * The text displayed as the header of the dialog.
   */
  title: String,
  /**
   * The confirm button text.
   */
  confirmButtonText: {
    type: String
  },
  /**
   * The cancel button text.
   */
  cancelButtonText: {
    type: String
  },
  /**
   * Specifies the width of the dialog.
   *
   */
  width: [String, Number],
  /**
   * Specifies the height of the dialog.
   * If not specified, the dialog will take the default height.
   */
  height: [String, Number],
});
const modelValue = defineModel('modelValue');
const {t} = useI18n();
const emit = defineEmits(['confirm', 'cancel', 'input', 'update:modelValue']);
const {
  isDesktop,
  isMobile
} = useScreenSize();

const isVisible = ref(modelValue.value);
const confirmButtonTextComputed = computed(() => props.confirmButtonText ?? t('common.dataGrid.confirm'));
const cancelButtonTextComputed = computed(() => props.cancelButtonText ?? t('common.dataGrid.cancel'));

function confirm() {
  isVisible.value = false;
  /**
   * Triggers when the confirm action is clicked.
   */
  emit('confirm');
}

function cancel() {
  isVisible.value = false;
  /**
   * Triggers when the cancel action is clicked.
   */
  emit('cancel');
}

watch(modelValue, async (newValue) => {
  isVisible.value = newValue;
});
watch(isVisible, async (newValue) => {
  if (!newValue) {
    cancel();
  }
  /**
   * Triggers when the value is changed.
   */
  emit('input', newValue);
  emit('update:modelValue', newValue);
});
</script>

<template>
  <en-dialog
      v-model="isVisible"
      :title="title"
      :width="width"
      :height="height"
      small
      hide-content-border
  >
    <template v-slot:content>
      <div class="en-confirm-dialog-content">
        <!-- @slot The dialog's content. -->
        <slot></slot>
      </div>
    </template>

    <template #actions>
      <div class="flex flex-wrap flex-auto justify-end mr-2 mt-2 mb-2">
        <en-button static-id="cancel-delete-button" class="mr-2" size="xs" @click="cancel" >
          {{ cancelButtonTextComputed }}
        </en-button>
        <en-button variant="default" static-id="confirm-delete-button" size="xs" @click="confirm" >
          {{ confirmButtonTextComputed }}
        </en-button>
      </div>
    </template>
  </en-dialog>
</template>

<style lang="scss" scoped>
.en-confirm-dialog-content {
  font-size: 1rem;
}
</style>